import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError, setToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged out, object=user
  const [tenantId, setTenantId] = useState(null); // super_admin can override
  const [pack, setPack] = useState(null);           // "essentials" | "growth" | …
  const [features, setFeatures] = useState(new Set());

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (!tenantId && data?.tenant_id) setTenantId(data.tenant_id);
      // Best-effort pack fetch (may fail on very first call if user was just created)
      try {
        const mp = await api.get("/sales/my-pack");
        setPack(mp.data.pack);
        setFeatures(new Set(mp.data.features || []));
      } catch (_) { setPack(null); setFeatures(new Set()); }
      return data;
    } catch (e) {
      setUser(false);
      setPack(null); setFeatures(new Set());
      return null;
    }
  }, [tenantId]);

  useEffect(() => {
    fetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setToken(data.access_token);
      const me = await fetchMe();
      return { ok: true, user: me };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setToken(null);
    setUser(false);
    setTenantId(null);
    setPack(null);
    setFeatures(new Set());
  };

  const hasFeature = (flag) => !flag || features.has(flag);

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, refreshMe: fetchMe, tenantId, setTenantId, pack, features, hasFeature }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
